import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardPlus, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { INTERVENTION_TYPES, timeAgo } from "@/pages/rpm/constants";

export default function RpmInterventions() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [pmap, setPmap] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/rpm/interventions", { params: { page_size: 200 } });
        setItems(r.data.items || []);
        const pids = Array.from(new Set((r.data.items || []).map((iv) => iv.patient_id)));
        const map = {};
        await Promise.all(pids.map(async (pid) => {
          try {
            const p = await api.get(`/patients/${pid}`);
            map[pid] = `${p.data.first_name} ${p.data.last_name} (${p.data.mrn})`;
          } catch {}
        }));
        setPmap(map);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((iv) => {
    if (!search) return true;
    const hay = `${INTERVENTION_TYPES[iv.intervention_type] || ""} ${iv.notes || ""} ${pmap[iv.patient_id] || ""}`.toLowerCase();
    return hay.includes(search.toLowerCase());
  });

  return (
    <div data-testid="rpm-interventions-page">
      <PageHeader title="RPM Interventions" subtitle="All clinical actions recorded against monitored patients." />
      <div className="hah-card p-3 mb-4 flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search interventions…"
            className="bg-transparent outline-none text-sm w-full" data-testid="rpm-intv-search" />
        </div>
      </div>
      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]">
          <ClipboardPlus className="w-8 h-8 mx-auto text-[#94A3B8] mb-2" /> No interventions recorded yet.
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((iv) => (
            <div key={iv.id} className="hah-card p-4 flex items-start justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-[#1F2937]">{INTERVENTION_TYPES[iv.intervention_type] || iv.intervention_type}</div>
                <div className="text-xs text-[#6B7280] mt-0.5">
                  <button onClick={() => navigate(`/rpm/patients/${iv.patient_id}`)} className="text-[#0B7E8C] hover:underline">
                    {pmap[iv.patient_id] || iv.patient_id}
                  </button>
                  {" · "}{timeAgo(iv.performed_at)}
                </div>
                {iv.notes && <div className="text-xs text-[#475569] mt-1">{iv.notes}</div>}
                {iv.outcome && <div className="text-xs text-[#0B7E8C] mt-0.5">Outcome: {iv.outcome}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
